import Swiper from "swiper";
import {
  A11y,
  FreeMode,
  Keyboard,
  Mousewheel,
  Scrollbar,
} from "swiper/modules";
import { initializeOnApproach } from "../../../resources/js/_initialize-on-approach";

document.addEventListener("DOMContentLoaded", function () {
  const carousels = document.querySelectorAll(".industries-carousel");

  carousels.forEach((carousel) => {
    initializeOnApproach(carousel, () => {
      initializeIndustriesCarouselPatternReveal(carousel);
      initializeIndustriesCarousel(carousel);
    });
  });
});

document.addEventListener("blockEditorInit", function (event) {
  const block = event.detail.block;

  if (!block) {
    return;
  }

  if (block.matches && block.matches(".industries-carousel")) {
    initializeIndustriesCarouselPatternReveal(block);
    initializeIndustriesCarousel(block);
  }

  block.querySelectorAll(".industries-carousel").forEach((carousel) => {
    initializeIndustriesCarouselPatternReveal(carousel);
    initializeIndustriesCarousel(carousel);
  });
});

function initializeIndustriesCarouselPatternReveal(carouselElement) {
  const patternElement = carouselElement.querySelector(".section-pattern__image");

  if (
    !patternElement ||
    carouselElement.dataset.industriesCarouselPatternInitialized === "true"
  ) {
    return;
  }

  carouselElement.dataset.industriesCarouselPatternInitialized = "true";

  if (!("IntersectionObserver" in window)) {
    carouselElement.classList.add("industries-carousel--pattern-visible");
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        carouselElement.classList.add("industries-carousel--pattern-visible");
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.25,
      rootMargin: "0px 0px -10% 0px",
    },
  );

  observer.observe(carouselElement);
}

function initializeIndustriesCarousel(carouselElement) {
  const viewportElement = carouselElement.querySelector(
    ".industries-carousel__viewport",
  );
  const swiperContainer = carouselElement.querySelector(
    ".industries-carousel__swiper",
  );
  const progressTrackElement = carouselElement.querySelector(
    ".industries-carousel__scrollbar",
  );

  if (
    !swiperContainer ||
    swiperContainer.dataset.industriesCarouselInitialized === "true"
  ) {
    return;
  }

  const slides = swiperContainer.querySelectorAll(".swiper-slide");

  if (slides.length < 2) {
    carouselElement.classList.add("industries-carousel--locked");
    return;
  }

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  const updateViewportWidth = () => {
    if (!viewportElement) {
      return;
    }

    const viewportBounds = viewportElement.getBoundingClientRect();
    // Use clientWidth (excludes the vertical scrollbar) so the full-bleed viewport
    // ends exactly at the visible edge — otherwise the last card is clipped.
    const viewportWidth =
      document.documentElement.clientWidth || window.innerWidth;
    const availableWidth = Math.max(0, viewportWidth - viewportBounds.left);

    viewportElement.style.setProperty(
      "--industries-carousel-visible-width",
      `${availableWidth}px`,
    );
  };

  const syncLockState = (swiperInstance) => {
    carouselElement.classList.toggle(
      "industries-carousel--locked",
      swiperInstance.isLocked,
    );
  };

  updateViewportWidth();
  swiperContainer.dataset.industriesCarouselInitialized = "true";

  const swiper = new Swiper(swiperContainer, {
    modules: [A11y, FreeMode, Keyboard, Mousewheel, Scrollbar],
    slidesPerView: 3.75,
    spaceBetween: 24,
    slidesOffsetAfter: 96,
    speed: prefersReducedMotion ? 0 : 700,
    freeMode: {
      enabled: true,
      momentumRatio: prefersReducedMotion ? 0 : 0.35,
    },
    watchOverflow: true,
    grabCursor: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    mousewheel: {
      enabled: true,
      forceToAxis: true,
      releaseOnEdges: true,
    },
    scrollbar: progressTrackElement
      ? {
          el: progressTrackElement,
          dragClass: "industries-carousel__scrollbar-thumb",
          draggable: true,
          hide: false,
          snapOnRelease: false,
        }
      : false,
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous industries",
      nextSlideMessage: "Next industries",
    },
    breakpoints: {
      0: {
        slidesPerView: 1.12,
        spaceBetween: 16,
        slidesOffsetAfter: 48,
      },
      768: {
        slidesPerView: 2.25,
        spaceBetween: 20,
        slidesOffsetAfter: 64,
      },
      992: {
        slidesPerView: 3.75,
        spaceBetween: 24,
        slidesOffsetAfter: 96,
      },
    },
    on: {
      init() {
        syncLockState(this);
      },
      resize() {
        syncLockState(this);
      },
      lock() {
        syncLockState(this);
      },
      unlock() {
        syncLockState(this);
      },
    },
  });

  requestAnimationFrame(() => {
    updateViewportWidth();
    swiper.update();
    syncLockState(swiper);
  });

  window.addEventListener("resize", () => {
    updateViewportWidth();
    swiper.update();
    syncLockState(swiper);
  });
}
